import AdSlot from './AdSlot';
import AdLocationRegistry from './AdLocationRegistry';

export default class AdService {
    constructor({ publisherId, service, controls = [], domTracking = false }) {
        this.publisherId = publisherId;
        this.service = service;
        this.controls = controls;
        this.slots = {}; // Active slot instances
        this.registeredSlotIds = new Set(); // Track which slot IDs have been registered globally
        this.renderedAds = new Map(); // Track rendered ads with their HTML content
        this.locationQueue = new Set(); // Queue locations that need to be fetched
        this.domTracking = domTracking;
        this.fetchInProgress = false;
        this.debounceTimer = null;
        this.observers = new Map(); // Store intersection observers for slots
        this.cordlessMediaSlots = new Set(); // Track Cordless Media ad slots

        // Bind methods to preserve 'this' context
        this.debouncedFetchAds = this.debouncedFetchAds.bind(this);

        // Watch for changes in the ads store
        this.setupStoreWatcher();
    }

    // Set up watcher for ads store changes
    setupStoreWatcher() {
        const setupWatcher = () => {
            if (window.store) {
                // Watch for ads being added to the store
                window.store.subscribe((mutation) => {
                    if (mutation.type === 'ads/ADD_AD' || mutation.type === 'ads/ADD_MULTIPLE_ADS') {
                        // Check if any of our slots can now be loaded
                        setTimeout(() => {
                            this.checkSlotsForNewAds();
                        }, 100);
                    }
                });
            } else {
                // Retry if store not available yet
                setTimeout(setupWatcher, 1000);
            }
        };

        setupWatcher();
    }

    // Check if any slots can be loaded with newly available ads
    checkSlotsForNewAds() {
        let loadedCount = 0;

        Object.keys(this.slots).forEach(id => {
            const slot = this.slots[id];
            if (slot && slot.canBeLoaded && slot.canBeLoaded()) {
                this.loadSlot(id);
                loadedCount++;
            }
        });
    }

    // Queue a location for fetching and debounce the actual fetch
    requestLocation(location) {
        if (!location) return;

        this.locationQueue.add(location);
        this.debouncedFetchAds();
    }

    // Debounced fetch to batch multiple location requests
    debouncedFetchAds() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(async () => {
            if (this.locationQueue.size > 0 && !this.fetchInProgress) {
                const locations = Array.from(this.locationQueue);
                this.locationQueue.clear();
                await this.fetchAds(locations);
            }
        }, 100); // 100ms debounce
    }

    async fetchAds(locations) {
        if (this.fetchInProgress) return;
        this.fetchInProgress = true;

        try {
            // Only fetch locations we don't already have
            const unfetchedLocations = locations.filter((loc) => !this.hasAdsForLocation(loc));

            if (unfetchedLocations.length === 0) {
                this.fetchInProgress = false;
                return;
            }

            const response = await this.service.fetchAds(unfetchedLocations);

            if (response && Array.isArray(response)) {
                // Store ads in the dedicated ads store
                response.forEach((ad) => {
                    if (window.store && window.store.dispatch) {
                        window.store.dispatch('ads/addAd', ad);
                    }

                    // Extract ID and store in our local cache
                    const match = ad.html && ad.html.match(/id=['"]([^'"]+)['"]/);
                    if (match) {
                        const adId = match[1];
                        this.renderedAds.set(adId, {
                            ...ad,
                            rendered: false,
                            element: null
                        });
                    }
                });

                // Inject ads into waiting slots
                this.injectWaitingSlots();
            }
        } catch (error) {
            // console.error('AdService: Error fetching ads:', error);
        } finally {
            this.fetchInProgress = false;
        }
    }

    // Check if we have ads for a specific location
    hasAdsForLocation(location) {
        if (window.store && window.store.getters) {
            return window.store.getters['ads/hasAdsForLocation'](location);
        }
        return false;
    }

    // Register a slot with lazy loading
    registerSlot({ id, element, type, location, showLoader = true }) {
        if (!element || !id) return;

        // Clean up existing slot if it exists
        if (this.slots[id]) {
            this.destroySlot(id);
        }

        // Check if IntersectionObserver is supported
        const hasIntersectionObserver = typeof window !== 'undefined' && 'IntersectionObserver' in window;

        if (hasIntersectionObserver) {
            // Create intersection observer for lazy loading
            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !this.renderedAds.get(id)?.rendered) {
                        this.loadSlot(id);
                        observer.disconnect(); // Stop observing once loaded
                    }
                });
            }, {
                threshold: 0.1, // Trigger when 10% visible
                rootMargin: '50px' // Start loading 50px before entering viewport
            });

            observer.observe(element);
            this.observers.set(id, observer);
        } else {
            // Fallback: Load immediately if IntersectionObserver not supported
            // Delay slightly to allow DOM to settle
            setTimeout(() => {
                this.loadSlot(id);
            }, 100);
        }

        // Show loader if requested
        if (showLoader && element.innerHTML.trim() === '') {
            element.innerHTML = '<div class="ad-skeleton-loader">Loading ad...</div>';
        }

        // Create slot instance
        const slot = new AdSlot({
            id,
            element,
            type,
            location,
            service: this.service,
            controls: this.controls
        });

        this.slots[id] = slot;
        this.registeredSlotIds.add(id);

        // Request ads for this location if not already done
        if (location) {
            this.requestLocation(location);
        }

        return slot;
    }

    // Load a specific slot with its ad
    async loadSlot(id) {
        const slot = this.slots[id];
        if (!slot || !slot.element) return;

        // Check if we have the ad data
        let adData = this.getAdForSlot(id);

        if (!adData) {
            // Request location and wait briefly for response
            if (slot.location) {
                this.requestLocation(slot.location);
                // Wait a bit for the fetch to complete
                await new Promise(resolve => setTimeout(resolve, 200));
                adData = this.getAdForSlot(id);
            }

            if (!adData) {
                return;
            }
        }

        // Track if this is a Cordless Media ad
        if (this.isCordlessMediaAd(adData)) {
            this.cordlessMediaSlots.add(id);
        }

        // Mark as rendered to prevent duplicate loading
        if (this.renderedAds.has(id)) {
            this.renderedAds.get(id).rendered = true;
            this.renderedAds.get(id).element = slot.element;
        }

        // Mark as rendered in the store
        if (window.store && window.store.dispatch) {
            window.store.dispatch('ads/markAdRendered', id);
        }

        // Load the ad through the slot
        await slot.load(adData);

        // Run control callbacks
        this.controls.forEach(ctrl => ctrl.onLoad?.(slot));
    }

    // Get ad data for a specific slot
    getAdForSlot(id) {
        // First check our local cache
        if (this.renderedAds.has(id)) {
            return this.renderedAds.get(id);
        }

        // Check the dedicated ads store using getters
        if (window.store && window.store.getters) {
            const ad = window.store.getters['ads/getAdById'](id);
            if (ad) {
                // Cache it locally for faster access
                this.renderedAds.set(id, { ...ad, rendered: false });
                return ad;
            }
        }

        // Also check if there are any ads for this location that haven't been mapped to this slot yet
        if (window.store && window.store.getters) {
            const allAds = window.store.getters['ads/allAds'];
            // Find an ad that matches this slot's location and hasn't been used yet
            const potentialAd = allAds.find(ad => {
                // Check if this ad matches our location (gallery ads for gallery slots)
                const slotLocation = this.slots[id]?.location || '';
                if (ad.location !== slotLocation) return false;

                // Check if this ad has an ID in its HTML
                if (!ad.html) return false;
                const match = ad.html.match(/id=['"]([^'"]+)['"]/);
                if (!match) return false;

                // Check if this specific ad ID matches our slot ID
                return match[1] === id;
            });

            if (potentialAd) {
                this.renderedAds.set(id, { ...potentialAd, rendered: false });
                return potentialAd;
            }
        }

        return null;
    }

    // Inject ads into slots that are waiting
    injectWaitingSlots() {
        Object.keys(this.slots).forEach(id => {
            const slot = this.slots[id];
            const adData = this.renderedAds.get(id);

            if (slot && adData && !adData.rendered && this.isElementInViewport(slot.element)) {
                this.loadSlot(id);
            }
        });
    }

    // Check if element is in viewport (for immediate loading of visible ads)
    isElementInViewport(element) {
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    }

    // Check if an ad is a Cordless Media ad
    isCordlessMediaAd(adData) {
        if (!adData) return false;

        // Check if the callback contains Cordless Media patterns
        if (adData.callback && (
            adData.callback.includes('cmWrapper.ads.defineUnit') ||
            adData.callback.includes('cmWrapper.ads.requestUnits')
        )) {
            return true;
        }

        // Check if the HTML contains Cordless Media patterns
        if (adData.html && (
            adData.html.includes('data-cordless') ||
            adData.html.includes('cordlessmedia')
        )) {
            return true;
        }

        return false;
    }

    // Get modal Cordless Media ad IDs
    getModalCordlessMediaAds() {
        const modalLocations = ['ldp_header', 'ldp_middle', 'ldp_footer'];
        return Array.from(this.cordlessMediaSlots).filter(id => {
            const slot = this.slots[id];
            return slot && modalLocations.includes(slot.location);
        });
    }

    // Remove destroyed ads from tracking
    removeFromTracking(adIds) {
        if (Array.isArray(adIds)) {
            adIds.forEach(id => {
                this.cordlessMediaSlots.delete(id);
                // Also remove from rendered ads cache
                this.renderedAds.delete(id);
            });
        }
    }

    // Destroy a specific slot
    destroySlot(id) {
        const slot = this.slots[id];
        if (slot) {
            slot.destroy();
            delete this.slots[id];

            // Remove from Cordless Media tracking
            this.cordlessMediaSlots.delete(id);

            // Clean up observer
            const observer = this.observers.get(id);
            if (observer) {
                observer.disconnect();
                this.observers.delete(id);
            }

            // Run control callbacks
            this.controls.forEach(ctrl => ctrl.onDestroy?.(slot));
        }
    }

    // Clean up for pagination - preserve rendered ads but reset slots
    cleanupForPagination() {
        // Reset rendered state in store for pagination
        if (window.store && window.store.dispatch) {
            window.store.dispatch('ads/resetRenderedState');
        }

        // Destroy all slots but keep rendered ads in cache
        Object.keys(this.slots).forEach(id => {
            const slot = this.slots[id];
            if (slot && slot.element && document.body.contains(slot.element)) {
                // Clear the element but preserve the rendered ad data
                slot.element.innerHTML = '<div class="ad-skeleton-loader">Loading ad...</div>';
            }
            this.destroySlot(id);
        });

        // Clear Cordless Media tracking since slots are destroyed
        this.cordlessMediaSlots.clear();

        // Reset Cordless Media manager state to allow new ads to be processed
        if (window.cordlessMediaManager) {
            // Clear any existing timeout
            if (window.cordlessMediaManager.requestUnitsTimeout) {
                clearTimeout(window.cordlessMediaManager.requestUnitsTimeout);
            }

            // Remove any existing requestUnits script to prevent conflicts
            const existingRequestScript = document.querySelector('script[data-cordless-request-units="true"]');
            if (existingRequestScript) {
                existingRequestScript.remove();
            }

            // Clear pending units and reset the scheduled flag
            window.cordlessMediaManager.pendingUnits.clear();
            if (window.cordlessMediaManager.processedUnits) {
                window.cordlessMediaManager.processedUnits.clear();
            }
            window.cordlessMediaManager.requestUnitsScheduled = false;
            window.cordlessMediaManager.requestUnitsParams = null;
        }

        // Don't clear renderedAds or registeredSlotIds
        // This allows us to reuse ads on new pages
    }

    // Complete cleanup - use when component is destroyed
    destroy() {
        Object.keys(this.slots).forEach(id => this.destroySlot(id));
        this.slots = {};
        this.cordlessMediaSlots.clear();
        this.observers.clear();
        clearTimeout(this.debounceTimer);
    }

    // Check if element is in DOM
    isInDOM(element) {
        return element && document.body.contains(element);
    }

    // Inject all registered slots (legacy compatibility)
    injectAll() {
        Object.keys(this.slots).forEach(id => {
            const slot = this.slots[id];
            if (slot && this.isInDOM(slot.element)) {
                this.loadSlot(id);
            }
        });
    }
}
